
import { makeModalDraggable } from './mapUtils.js';

const FLOATING_DIALOG_SELECTORS = [
  '#draw-panel',
  '#pc-panel',
  '#search-form',
  '#layer-info-panel',
  '#bookmark-panel',
  '#upload-panel',
  '#mapping-modal',
  '#draw-summary-panel',
  '#pc-summary-panel',
  '#floating-table-panel',
  '.sevenn-dialog'
].join(',');

let floatingDialogZIndex = 4100;
let floatingDialogStackingInitialized = false;
let dialogToolActiveStateInitialized = false;
let lockedSidebarToolSelector = null;

const SIDEBAR_TOOL_BUTTON_SELECTOR = [
  '.draw-panel-toggle',
  '.pc-panel-toggle',
  '.query-button',
  '#layer-info-btn',
  '.upload-button',
  '.bookmark-button',
  '.info-button',
  '.pan-button',
  '.zoom-box-button'
].join(',');

const PANEL_SIDEBAR_TOOL_MAP = [
  ['draw-panel', '.draw-panel-toggle'],
  ['draw-summary-panel', '.draw-panel-toggle'],
  ['pc-panel', '.pc-panel-toggle'],
  ['search-form', '.query-button'],
  ['layer-info-panel', '#layer-info-btn'],
  ['bookmark-panel', '.bookmark-button']
];

const DIALOG_TOOL_BUTTON_SELECTOR = [
  '#draw-panel .draw-polygon',
  '#draw-panel .draw-circle',
  '#draw-panel #clear-address-selection',
  '#draw-panel #load-previous-results',
  '#pc-panel .pc4-select',
  '#pc-panel .nis6-select',
  '#pc-panel .pc6-select',
  '#pc-panel .pc-polygon',
  '#pc-panel .pc-circle',
  '#pc-summary-panel .pc-clear-selection',
  '#pc-panel .pc-current-results'
].join(',');

function isVisibleBox(element) {
  const style = getComputedStyle(element);
  const rect = element.getBoundingClientRect();

  return style.display !== 'none' &&
    style.visibility !== 'hidden' &&
    Number(style.opacity || 1) !== 0 &&
    rect.width > 0 &&
    rect.height > 0;
}

function intersectionArea(a, b) {
  const width = Math.max(0, Math.min(a.right, b.right) - Math.max(a.left, b.left));
  const height = Math.max(0, Math.min(a.bottom, b.bottom) - Math.max(a.top, b.top));
  return width * height;
}

function getSidebarSelectorForDialog(dialog) {
  if (!dialog) return null;
  const panelMatch = PANEL_SIDEBAR_TOOL_MAP.find(([panelId]) => dialog.id === panelId);
  return panelMatch?.[1] || null;
}

function getSidebarButton(selectorOrButton) {
  return typeof selectorOrButton === 'string'
    ? document.querySelector(selectorOrButton)
    : selectorOrButton;
}

function getSidebarSelectorForButton(button) {
  if (!button) return null;
  return SIDEBAR_TOOL_BUTTON_SELECTOR
    .split(',')
    .map(selector => selector.trim())
    .find(selector => button.matches?.(selector)) || null;
}

function clearSidebarButtonStates() {
  document.querySelectorAll(SIDEBAR_TOOL_BUTTON_SELECTOR).forEach(button => {
    button.classList.remove('active', 'active-tool');
    button.setAttribute('aria-pressed', 'false');
  });
}

function syncSidebarCommandLockClass() {
  document.documentElement.classList.toggle('sidebar-command-locked', Boolean(lockedSidebarToolSelector));
}

function clearActiveDialogToolButtons() {
  document.querySelectorAll(DIALOG_TOOL_BUTTON_SELECTOR).forEach(button => {
    button.classList.remove('active', 'active-tool', 'active-edit-vertex');
    button.setAttribute('aria-pressed', 'false');
    button.style.removeProperty('background');
    button.style.removeProperty('background-color');
    button.style.removeProperty('background-image');
    button.style.removeProperty('border-color');
    button.style.removeProperty('color');
    button.style.removeProperty('box-shadow');
  });
}

function setActiveDialogToolButton(button) {
  if (!button) return;
  button.classList.add('active', 'active-tool', 'active-edit-vertex');
  button.setAttribute('aria-pressed', 'true');
  button.style.setProperty('background', 'var(--sevenn-primary)', 'important');
  button.style.setProperty('background-color', 'var(--sevenn-primary)', 'important');
  button.style.setProperty('background-image', 'none', 'important');
  button.style.setProperty('border-color', 'var(--sevenn-primary)', 'important');
  button.style.setProperty('color', '#fff', 'important');
  button.style.setProperty('box-shadow', '0 8px 18px rgba(59, 57, 232, 0.24)', 'important');
}

function syncDialogHierarchyWidget(dialog, active = true) {
  if (dialog?.id !== 'draw-summary-panel') return;

  const currentResultsButton = document.getElementById('load-previous-results');
  if (!active) {
    currentResultsButton?.classList.remove('active', 'active-tool', 'active-edit-vertex');
    currentResultsButton?.setAttribute('aria-pressed', 'false');
    return;
  }

  clearActiveDialogToolButtons();
  setActiveDialogToolButton(currentResultsButton);
}

function switchSidebarContext(selector) {
  if (!selector || !lockedSidebarToolSelector || selector === lockedSidebarToolSelector) return;

  document.dispatchEvent(new CustomEvent('application-reset-active-tools', {
    detail: { preserveAddressAidsResults: true }
  }));
}

function syncVisiblePanelSidebarTool(changedDialog = null) {
  if (lockedSidebarToolSelector) return;

  const changedSelector = getSidebarSelectorForDialog(changedDialog);
  const activeSidebarTool = Array.from(document.querySelectorAll(SIDEBAR_TOOL_BUTTON_SELECTOR))
    .find(button => button.classList.contains('active-tool'));

  if (changedDialog && changedSelector && !isVisibleBox(changedDialog)) {
    syncDialogHierarchyWidget(changedDialog, false);
    const changedButton = document.querySelector(changedSelector);
    if (activeSidebarTool === changedButton) {
      const visiblePanels = PANEL_SIDEBAR_TOOL_MAP
        .map(([panelId, sidebarSelector]) => ({
          panel: document.getElementById(panelId),
          sidebarSelector
        }))
        .filter(item => item.panel && isVisibleBox(item.panel))
        .sort((a, b) => {
          const az = Number.parseInt(getComputedStyle(a.panel).zIndex, 10) || 0;
          const bz = Number.parseInt(getComputedStyle(b.panel).zIndex, 10) || 0;
          return bz - az;
        });

      if (visiblePanels[0]) {
        setActiveSidebarTool(visiblePanels[0].sidebarSelector);
      } else {
        clearActiveSidebarTools();
      }
    }
    return;
  }

  if (changedSelector && changedDialog && isVisibleBox(changedDialog)) {
    setActiveSidebarTool(changedSelector);
  }
}

export function clearActiveSidebarTools() {
  lockedSidebarToolSelector = null;
  syncSidebarCommandLockClass();
  clearSidebarButtonStates();
}

export function setActiveSidebarTool(selectorOrButton, options = {}) {
  const button = getSidebarButton(selectorOrButton);

  if (!button) return;

  const selector = typeof selectorOrButton === 'string'
    ? selectorOrButton
    : getSidebarSelectorForButton(button);

  if (lockedSidebarToolSelector && !options.lock && selector !== lockedSidebarToolSelector) {
    return;
  }

  if (options.lock) {
    lockedSidebarToolSelector = selector;
    syncSidebarCommandLockClass();
  }

  clearSidebarButtonStates();
  document.querySelectorAll(SIDEBAR_TOOL_BUTTON_SELECTOR).forEach(sidebarButton => {
    if (sidebarButton !== button && document.activeElement === sidebarButton) {
      sidebarButton.blur();
    }
  });
  button.classList.add('active', 'active-tool');
  button.setAttribute('aria-pressed', 'true');
}

export function setSidebarToolActive(selectorOrButton, active) {
  if (active) {
    setActiveSidebarTool(selectorOrButton);
    return;
  }

  const button = getSidebarButton(selectorOrButton);
  const selector = typeof selectorOrButton === 'string'
    ? selectorOrButton
    : getSidebarSelectorForButton(button);

  if (selector && selector === lockedSidebarToolSelector) {
    lockedSidebarToolSelector = null;
    syncSidebarCommandLockClass();
  }

  button?.classList.remove('active', 'active-tool');
  button?.setAttribute('aria-pressed', 'false');
}

export function bringFloatingDialogToFront(dialog) {
  if (!dialog) return;
  const activeZIndexes = Array.from(document.querySelectorAll(FLOATING_DIALOG_SELECTORS))
    .map(element => Number.parseInt(getComputedStyle(element).zIndex, 10))
    .filter(Number.isFinite);
  const maxActiveZIndex = activeZIndexes.length ? Math.max(...activeZIndexes) : floatingDialogZIndex;
  floatingDialogZIndex = Math.max(floatingDialogZIndex, maxActiveZIndex) + 1;
  dialog.style.setProperty('z-index', String(floatingDialogZIndex), 'important');

  let parent = dialog.parentElement;
  while (parent && parent !== document.body) {
    const style = getComputedStyle(parent);
    if (style.position !== 'static' && style.zIndex !== 'auto') {
      parent.style.setProperty('z-index', String(floatingDialogZIndex), 'important');
    }
    parent = parent.parentElement;
  }

  syncVisiblePanelSidebarTool(dialog);
}

export function placeFloatingDialog(dialog, options = {}) {
  if (!dialog) return;
  const margin = options.margin ?? 16;
  const topMargin = options.topMargin ?? 68;
  const leftMargin = options.leftMargin ?? 72;
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const measuredRect = dialog.getBoundingClientRect();
  const width = measuredRect.width || dialog.offsetWidth;
  const height = measuredRect.height || dialog.offsetHeight;
  if (!width || !height) {
    bringFloatingDialogToFront(dialog);
    return;
  }

  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
  const centerX = (viewportWidth - width) / 2;
  const centerY = (viewportHeight - height) / 2;
  const maxLeft = Math.max(leftMargin, viewportWidth - width - margin);
  const maxTop = Math.max(topMargin, viewportHeight - height - margin);
  const mapRect = document.getElementById('map')?.getBoundingClientRect?.() ?? {
    left: 0,
    right: viewportWidth,
    top: 0,
    bottom: viewportHeight
  };
  const mapCenterX = mapRect.left + (mapRect.width || viewportWidth) / 2;
  const rightEdge = Math.min(mapRect.right || viewportWidth, viewportWidth) - width - margin;
  const rightInner = rightEdge - width - margin;
  const mapCenter = mapRect.left + ((mapRect.width || viewportWidth) - width) / 2;

  const xValues = [
    rightEdge,
    rightInner,
    mapCenter,
    centerX,
    leftMargin + width + margin,
    leftMargin
  ];
  const yValues = [
    topMargin,
    topMargin + height + margin,
    centerY,
    viewportHeight - (height * 2) - margin,
    viewportHeight - height - margin
  ];

  const occupiedRects = Array.from(document.querySelectorAll(FLOATING_DIALOG_SELECTORS))
    .filter(element => element !== dialog && !dialog.contains(element)
      && !options.ignoreDialogs?.has(element) && isVisibleBox(element))
    .map(element => element.getBoundingClientRect());

  // Dialogs have different sizes; use their actual edges to find free gaps.
  occupiedRects.forEach(rect => {
    xValues.push(rect.right + margin, rect.left - width - margin, rect.left);
    yValues.push(rect.bottom + margin, rect.top - height - margin, rect.top);
  });

  const rawCandidates = xValues.flatMap(x => yValues.map(y => [x, y]));

  const candidates = rawCandidates.map(([x, y]) => ({
    left: clamp(x, leftMargin, maxLeft),
    top: clamp(y, topMargin, maxTop)
  }));



  const best = candidates
    .map(candidate => {
      const rect = {
        left: candidate.left,
        top: candidate.top,
        right: candidate.left + width,
        bottom: candidate.top + height
      };
      const overlap = occupiedRects.reduce((total, occupied) => total + intersectionArea(rect, occupied), 0);
      const rightDistance = Math.max(0, maxLeft - candidate.left);
      const leftSidePenalty = candidate.left + width / 2 < mapCenterX ? 25000 : 0;
      const verticalDistance = Math.abs(candidate.top - centerY);

      const hasOverlap = overlap > 0;

      return {
        ...candidate,
        score: (hasOverlap ? 100000000 : 0) + overlap * 1000 + leftSidePenalty + rightDistance * 10 + verticalDistance
      };
    })
    .sort((a, b) => a.score - b.score)[0];

  dialog.style.setProperty('position', 'fixed', 'important');
  dialog.style.setProperty('margin', '0', 'important');
  dialog.style.setProperty('left', `${best.left}px`, 'important');
  dialog.style.setProperty('top', `${best.top}px`, 'important');
  dialog.style.setProperty('right', 'auto', 'important');
  dialog.style.setProperty('bottom', 'auto', 'important');
  dialog.style.setProperty('transform', 'none', 'important');
  dialog.style.visibility = '';
  bringFloatingDialogToFront(dialog);

  if (!options.layoutPass) scheduleFloatingDialogLayout();
}

let floatingLayoutQueued = false;
function scheduleFloatingDialogLayout() {
  if (floatingLayoutQueued) return;
  floatingLayoutQueued = true;
  requestAnimationFrame(() => {
    floatingLayoutQueued = false;
    const dialogs = Array.from(document.querySelectorAll(FLOATING_DIALOG_SELECTORS))
      .filter(isVisibleBox);
    const overlaps = dialogs.some((dialog, index) => dialogs.slice(index + 1).some(other =>
      !dialog.contains(other) && !other.contains(dialog)
      && intersectionArea(dialog.getBoundingClientRect(), other.getBoundingClientRect()) > 0));
    if (!overlaps) return;
    // Place the largest windows first, considering only windows already placed.
    // This allows existing windows to move when a new window cannot fit a gap.
    dialogs.sort((a, b) => b.offsetWidth * b.offsetHeight - a.offsetWidth * a.offsetHeight);
    const pending = new Set(dialogs);
    dialogs.forEach(dialog => {
      pending.delete(dialog);
      placeFloatingDialog(dialog, { ignoreDialogs: pending, layoutPass: true });
    });
  });
}

// Startup opens tool panels and both summaries concurrently. Wait for fonts
// and a painted frame so final header/table dimensions are used for placement.
export async function settleInitialSelectionDialogs() {
  await document.fonts?.ready;
  await new Promise(resolve => requestAnimationFrame(resolve));
  await new Promise(resolve => requestAnimationFrame(resolve));
  // Reserve space for the large summaries first, then place the compact tools.
  const dialogs = Array.from(document.querySelectorAll(FLOATING_DIALOG_SELECTORS))
    .filter(isVisibleBox)
    .sort((a, b) => b.offsetWidth * b.offsetHeight - a.offsetWidth * a.offsetHeight);
  const pending = new Set(dialogs);
  dialogs.forEach(dialog => {
    pending.delete(dialog);
    placeFloatingDialog(dialog, { ignoreDialogs: pending, layoutPass: true });
  });
}

function observeFloatingDialogLayout() {
  const states = new WeakMap();
  const resizeObserver = typeof ResizeObserver === 'undefined' ? null
    : new ResizeObserver(scheduleFloatingDialogLayout);
  const scan = () => {
    let changed = false;
    document.querySelectorAll(FLOATING_DIALOG_SELECTORS).forEach(dialog => {
      const visible = isVisibleBox(dialog);
      if (!states.has(dialog)) resizeObserver?.observe(dialog);
      if (states.get(dialog) !== visible) changed = true;
      states.set(dialog, visible);
    });
    if (changed) scheduleFloatingDialogLayout();
  };
  new MutationObserver(scan).observe(document.body, {
    childList: true, subtree: true, attributes: true, attributeFilter: ['style', 'class', 'hidden']
  });
  window.addEventListener('resize', scheduleFloatingDialogLayout);
  scan();
}

export function initFloatingDialogStacking() {
  if (floatingDialogStackingInitialized) return;
  floatingDialogStackingInitialized = true;
  observeFloatingDialogLayout();

  document.addEventListener('pointerdown', (event) => {
    const dialog = event.target.closest?.(FLOATING_DIALOG_SELECTORS);
    if (dialog && isVisibleBox(dialog)) {
      const sidebarSelector = getSidebarSelectorForDialog(dialog);
      if (dialog.id === 'draw-summary-panel' && lockedSidebarToolSelector) {
        document.dispatchEvent(new CustomEvent('application-reset-active-tools', {
          detail: { preserveAddressAidsResults: true }
        }));
      } else {
        switchSidebarContext(sidebarSelector);
      }
      bringFloatingDialogToFront(dialog);
      setActiveSidebarTool(sidebarSelector);
      syncDialogHierarchyWidget(dialog);
      return;
    }

    const sidebarButton = event.target.closest?.(SIDEBAR_TOOL_BUTTON_SELECTOR);
    if (!sidebarButton) return;

    const sidebarSelector = getSidebarSelectorForButton(sidebarButton);
    switchSidebarContext(sidebarSelector);
    setActiveSidebarTool(sidebarButton);
  }, true);

  document.addEventListener('click', event => {
    const dialog = event.target.closest?.(FLOATING_DIALOG_SELECTORS);
    const sidebarSelector = getSidebarSelectorForDialog(dialog);
    if (!dialog || !sidebarSelector) return;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!dialog.isConnected || !isVisibleBox(dialog)) return;
        setActiveSidebarTool(sidebarSelector);
        syncDialogHierarchyWidget(dialog);
      });
    });
  });
}

export function initDialogToolActiveState() {
  if (dialogToolActiveStateInitialized) return;
  dialogToolActiveStateInitialized = true;

  document.addEventListener('click', event => {
    const button = event.target.closest?.(DIALOG_TOOL_BUTTON_SELECTOR);
    if (!button || button.disabled || button.getAttribute('aria-disabled') === 'true') return;

    requestAnimationFrame(() => {
      if (!button.isConnected || button.disabled || button.getAttribute('aria-disabled') === 'true') return;
      clearActiveDialogToolButtons();
      setActiveDialogToolButton(button);
    });
  }, true);

  const observePanelVisibility = () => {
    PANEL_SIDEBAR_TOOL_MAP.forEach(([panelId]) => {
      const panel = document.getElementById(panelId);
      if (!panel || panel.__sevennSidebarToolObserved) return;

      panel.__sevennSidebarToolObserved = true;
      new MutationObserver(() => syncVisiblePanelSidebarTool(panel))
        .observe(panel, { attributes: true, attributeFilter: ['class', 'style'] });
    });
  };

  observePanelVisibility();
  new MutationObserver(observePanelVisibility)
    .observe(document.body, { childList: true, subtree: true });

  document.addEventListener('application-reset-active-tools', clearActiveDialogToolButtons);
  document.addEventListener('application-reset-active-tools', clearActiveSidebarTools);
}

export function showInfoMessage(message, title = "Information") {
  const old = document.getElementById("info-message-overlay");
  if (old) old.remove();

  const overlay = document.createElement("div");
  overlay.id = "info-message-overlay";
  overlay.className = "sevenn-dialog-overlay";
  overlay.setAttribute("role", "presentation");

  const dialog = document.createElement("div");
  dialog.className = "sevenn-dialog";
  dialog.style.visibility = "hidden";
  dialog.setAttribute("role", "dialog");
  dialog.setAttribute("aria-modal", "true");
  dialog.setAttribute("aria-labelledby", "info-message-title");
  dialog.setAttribute("aria-describedby", "info-message-body");

  const header = document.createElement("div");
  header.className = "sevenn-dialog-header";

  const heading = document.createElement("h2");
  heading.id = "info-message-title";
  heading.className = "sevenn-dialog-title";
  heading.textContent = title;

  const closeBtn = document.createElement("button");
  closeBtn.type = "button";
  closeBtn.className = "sevenn-icon-button";
  closeBtn.setAttribute("aria-label", "Close");
  closeBtn.title = "Close";
  closeBtn.innerHTML = "&times;";
  closeBtn.onclick = () => overlay.remove();

  header.appendChild(heading);
  header.appendChild(closeBtn);

  const body = document.createElement("div");
  body.id = "info-message-body";
  body.className = "sevenn-dialog-body";
  body.textContent = message;

  const footer = document.createElement("div");
  footer.className = "sevenn-dialog-footer";

  const okBtn = document.createElement("button");
  okBtn.type = "button";
  okBtn.textContent = "OK";
  okBtn.className = "btn sevenn-primary-button";
  okBtn.onclick = () => overlay.remove();

  footer.appendChild(okBtn);
  dialog.appendChild(header);
  dialog.appendChild(body);
  dialog.appendChild(footer);
  overlay.appendChild(dialog);

  overlay.addEventListener("click", (event) => {
    if (event.target === overlay) overlay.remove();
  });

  const onKeyDown = (event) => {
    if (event.key === "Escape") {
      overlay.remove();
      document.removeEventListener("keydown", onKeyDown);
    }
  };
  document.addEventListener("keydown", onKeyDown);

  document.body.appendChild(overlay);
  requestAnimationFrame(() => {
    placeFloatingDialog(dialog);
    makeModalDraggable(dialog, header);
  });
  okBtn.focus();
}
